import React, { useState } from 'react';
import { Button, useNotify, useRefresh, useRedirect } from 'react-admin';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormGroup from '@mui/material/FormGroup';
import { gql } from 'apollo-boost';
import { client } from '../helpers/apolloClient';
import RoleMultiSelect from './roleMultiSelect';

// Creating an account is NOT a composed react-admin CREATE. The composed
// createUser mutation was removed by the escalation fix - with `password` gone
// from every user input the model required a credential no caller could supply,
// so it could only ever error - and re-adding `password` to the composed input
// would put account takeover back on a general write path.
//
// So this is a hand-written dialog calling createUserAccount, which is the same
// pattern as discontinueSponsershipDialog: a form that does not map onto the
// composed CRUD, so it does not pretend to.

const CREATE_USER_ACCOUNT = gql`
    mutation createUserAccount($email: String!, $password: String!, $roles: [String!]) {
        createUserAccount(email: $email, password: $password, roles: $roles) {
            id
            email
            roles
        }
    }
`;

// Mirrors MIN_PASSWORD_LENGTH in finadmin_server/server/api/graphql/users.js.
// The server is what enforces it; this is only here so the form does not let
// somebody fill in a form that is going to be refused.
const MIN_PASSWORD_LENGTH = 8;

const CreateUserDialog = () => {
    const notify = useNotify();
    const refresh = useRefresh();
    const redirect = useRedirect();

    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [roles, setRoles] = useState([]);
    const [saving, setSaving] = useState(false);

    const reset = () => {
        setEmail('');
        setPassword('');
        setRoles([]);
    };

    const handleClose = () => {
        setOpen(false);
        reset();
    };

    const handleConfirm = () => {
        setSaving(true);

        client.mutate({
            mutation: CREATE_USER_ACCOUNT,
            variables: { email, password, roles },
        }).then(({ data }) => {
            setSaving(false);
            handleClose();
            notify(`Account created for ${data.createUserAccount.email}`);
            // Straight to the new account: the next thing to do is assign roles.
            refresh();
            redirect(`/User/${data.createUserAccount.id}/show`);
        }).catch(e => {
            setSaving(false);
            notify(e.message, { type: 'warning' });
        });
    };

    const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;

    return (
        <>
            <Button variant="contained" onClick={() => setOpen(true)} label="New account" />
            <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}
                    aria-labelledby="create-user-title">
                <DialogTitle id="create-user-title">New account</DialogTitle>
                <DialogContent>
                    <FormGroup>
                        <TextField
                            value={email}
                            onChange={ev => setEmail(ev.target.value)}
                            label="Email"
                            margin="normal"
                        />
                        <TextField
                            value={password}
                            onChange={ev => setPassword(ev.target.value)}
                            label={`Initial password (at least ${MIN_PASSWORD_LENGTH} characters)`}
                            type="password"
                            error={tooShort}
                            helperText={tooShort ? 'Too short.' : ' '}
                            margin="normal"
                        />
                        <RoleMultiSelect
                            value={roles}
                            onChange={setRoles}
                            label="Roles"
                            labelId="new-user-roles-label"
                        />
                    </FormGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} label="Cancel" />
                    <Button
                        onClick={handleConfirm}
                        label="Create"
                        disabled={saving || !email || tooShort}
                    />
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CreateUserDialog;